import { useState } from 'react';
import { FORM_FIELD_TYPES, type FormFieldDefinition, type FormFieldType } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { Button, Field } from '@/components/ui';

const OPTION_TYPES: readonly FormFieldType[] = ['SELECT', 'RADIO'];

/**
 * Edits a form's field set while it is still a draft.
 *
 * Local state only, committed with one explicit save — the same reason a
 * roster editor is not: reordering or relabelling several fields at once is
 * one intent, not several independent writes.
 */
export function FieldEditor({
  fields,
  saving,
  onSave,
}: {
  fields: readonly FormFieldDefinition[];
  saving: boolean;
  onSave: (fields: readonly FormFieldDefinition[]) => void;
}) {
  const { t } = useI18n();
  const [draft, setDraft] = useState<FormFieldDefinition[]>([...fields]);

  const addField = () => {
    setDraft([
      ...draft,
      {
        id: `field_${Date.now().toString(36)}`,
        label: '',
        type: 'TEXT',
        required: false,
        options: null,
      },
    ]);
  };

  const updateField = (index: number, changes: Partial<FormFieldDefinition>) => {
    setDraft(draft.map((field, i) => (i === index ? { ...field, ...changes } : field)));
  };

  const removeField = (index: number) => {
    setDraft(draft.filter((_, i) => i !== index));
  };

  const isDirty = JSON.stringify(draft) !== JSON.stringify(fields);

  return (
    <div className="field-editor">
      {draft.map((field, index) => (
        <div key={field.id} className="form-grid field-editor-row">
          <Field
            label={t('forms.form.fieldLabel')}
            name={`label-${field.id}`}
            value={field.label}
            onChange={(e) => updateField(index, { label: e.target.value })}
          />

          <div className="field">
            <label className="field-label" htmlFor={`type-${field.id}`}>
              {t('forms.form.fieldType')}
            </label>
            <select
              id={`type-${field.id}`}
              className="field-input"
              value={field.type}
              onChange={(e) => updateField(index, { type: e.target.value as FormFieldType })}
            >
              {FORM_FIELD_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`forms.fieldType.${type}`)}
                </option>
              ))}
            </select>
          </div>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => updateField(index, { required: e.target.checked })}
            />
            <span>{t('forms.form.required')}</span>
          </label>

          {OPTION_TYPES.includes(field.type) && (
            <Field
              label={t('forms.form.fieldOptions')}
              hint={t('forms.form.fieldOptionsHint')}
              name={`options-${field.id}`}
              value={(field.options ?? []).join(', ')}
              onChange={(e) =>
                updateField(index, {
                  options: e.target.value
                    .split(',')
                    .map((option) => option.trim())
                    .filter((option) => option.length > 0),
                })
              }
            />
          )}

          <Button
            type="button"
            variant="ghost"
            onClick={() => removeField(index)}
            aria-label={`${t('common.actions.cancel')}: ${field.label || field.id}`}
          >
            {t('common.actions.cancel')}
          </Button>
        </div>
      ))}

      <div className="field-editor-actions">
        <Button type="button" variant="secondary" onClick={addField}>
          {t('forms.form.addField')}
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={!isDirty}
          loading={saving}
          onClick={() => onSave(draft)}
        >
          {t('common.actions.save')}
        </Button>
      </div>
    </div>
  );
}
